<template>
	<LayoutBase :has-fixed-group="true">
		<header id="content__header">
			<img
                class="flag"
                :src="resource.country.flag"
                v-if="resource.country"
                v-tippy="resource.country.name"
            />
			<h1>{{ resource.projectName }}</h1>
			<span v-if="resource.urgent" class="pill pill--urgent">Urgent</span>
			<span v-if="resource.respondBy" class="respond-by">{{ respondBy }}</span>

            <div class="actions">
                <router-link :to="{ name: 'share-opportunity', params: { opportunity_id: resource.id }}"
                             class="btn btn--outline" v-if="resource.can.edit">Share
                </router-link>
                <router-link :to="{ name: 'opportunity-preview', params: { opportunity_id: resource.id }}"
                             class="btn btn--primary" v-if="resource.can.edit">Edit
                </router-link>
            </div>
		</header>

		<section class="company-info">
			<h2 v-if="resource.companyName">{{ resource.companyName }}</h2>

			<ul class="grid post-details">
				<li v-if="resource.createdAt" class="col-6 col-md-3 detail">
					<span class="label">Posted</span>
					<span class="h3"><SvgIcon :icon="'calendar'" /> {{ resource.createdAt }}</span>
				</li>
				<li v-if="resource.website" class="col-6 col-md-3 detail">
					<span class="label">Website</span>
					<a :href="resource.website" target="_blank" class="h3"> <SvgIcon :icon="'link'" /> Click here</a>
				</li>
				<li class="col-6 col-md-3 detail">
					<span class="label">Opportunity Type</span>
					<span class="h3"><SvgIcon :icon="'lock'" /> {{ (resource.mandated)?"Mandated":"Non-Mandated" }}</span>
				</li>
				<li v-if="resource.dealType" class="col-6 col-md-3 detail">
					<span class="label">Deal Type</span>
					<span class="h3"><SvgIcon :icon="'deal'" /> {{ resource.dealType }}</span>
				</li>
				<li v-if="resource.location" class="col-6 col-md-3 detail">
					<span class="label">Location of Activities</span>
					<span class="h3"><SvgIcon :icon="'location'" /> {{ resource.location }}</span>
				</li>
				<!-- <li v-if="responseRate" class="col-6 col-md-3 detail">
					<span class="label">Response Rate</span>
					<span class="h3"><SvgIcon :icon="'location'" /> {{ responseRate }}</span>
				</li> -->
			</ul>
		</section>

		<hr />

		<section class="deal-info">
			<ul class="grid post-details">
                <li v-if="resource.primarySector" class="col-12 col-md-3 detail">
                    <span class="label">Main Sector</span>
                    <span class="sector">{{ resource.primarySector.name }}</span>
                </li>
                <li v-if="resource.sectors" class="col-12 col-md-9 detail">
                    <span class="label">Sectors</span>
                    <span class="sector" v-for="sector in resource.sectors">{{ sector.name }}</span>
                </li>
				<li v-if="resource.currencyInitial" class="col-6 col-md-3 detail">
					<span class="label">Currency</span>
					<strong><SvgIcon :icon="'money'" /> {{ resource.currencyInitial }}</strong>
				</li>
				<li v-if="resource.companyRevenue" class="col-6 col-md-3 detail">
					<span class="label">Revenue</span>
					<strong><SvgIcon :icon="'value'" /> {{ resource.companyRevenue }}m</strong>
				</li>
				<li v-if="resource.projectedValueFrom && resource.projectedValueTo" class="col-6 col-md-3 detail">
					<span class="label">Projected transaction value</span>
					<strong><SvgIcon :icon="'value'" /> {{ resource.projectedValueFrom }}m-{{ resource.projectedValueTo }}m</strong>
				</li>
			</ul>
		</section>

		<Accordion :items="formatAccordionData()" :start-open="true"></Accordion>

		<div class="card accordion__item" v-if="resource.documents && resource.documents.length" style="margin-top: 2rem;">
			<h3 class="accordion__title">Attachments</h3>
			<div class="accordion__copy" style="height:auto">
				<ul class="documents" v-if="resource.documents.length">
					<li v-for="doc in resource.documents">
                        <AttachmentDownload :attachment="doc"></AttachmentDownload>
					</li>
				</ul>
			</div>
		</div>

		<section class="grid">
			<div class="col-12 col-md-8 order-2 order-md-1">
				<div class="card">
                    <template v-if="resource.can.respond">
                        <OpportunityResponseForm ref="responseForm" :opportunity="resource.id" @submitted="addResponse" @update="updateResponse">
                        </OpportunityResponseForm>
                        <hr />
                    </template>

                    <template v-else-if="resource.can.feedback">
                        <OpportunityFeedbackForm ref="feedbackForm" :opportunity="resource.id" @submitted="addFeedback">
                        </OpportunityFeedbackForm>
                        <hr />
                    </template>

                    <template v-if="resource.feedback">
                        <div class="header">Feedback <span class="notification">!</span></div>
                        <p>{{ resource.feedback }}</p>
                        <hr />
                    </template>

                    <div class="header">Able to help <span class="notification">!</span></div>
                    <Response v-for="response in ableToHelp" ref="ableToHelp" :key="response.id" :response="response" @response:deleted="responseDeleted" @response:edit="populateForm"></Response>
                    <hr />
                    <div class="header">Unable to help <span class="notification">!</span></div>
                    <Response v-for="response in unableToHelp" ref="unableToHelp" :key="response.id" :response="response" @response:deleted="responseDeleted" @response:edit="populateForm"></Response>
				</div>
			</div>

			<div class="col-12 col-md-4 order-1 order-md-2">
                <MemberCard v-if="resource.member" :member="resource.member"></MemberCard>

				<div v-if="resource.lead" class="profile-list">
					<p class="profile-list__title">Lead User</p>

					<UserCard :user="resource.lead" :borderless="true"></UserCard>
				</div>
				<div v-if="filterLead(resource.team).length" class="profile-list">
					<p class="profile-list__title">Users also involved</p>

					<UserCard :user="profile" :borderless="true" :small="true"
					          v-for="profile in filterLead(resource.team)" :key="profile.id">
					</UserCard>
				</div>
			</div>
		</section>
	</LayoutBase>
</template>

<script>
	import LayoutBase from '../Layout/LayoutBase';
	import Response from '../Partials/Response';
	import API from './../../api';
	import SvgIcon from '../SvgIcon';
	import Accordion from '../lib/Accordion';
	import { mapState } from 'vuex';
	import FileUpload from '../Form/FileUpload';
	import ResourceView from '../ResourceView';
	import UserCard from '../User/UserCard';
	import format from 'date-fns/format';
	import OpportunityResponseForm from './Response/OpportunityResponseForm';
	import OpportunityFeedbackForm from './Feedback/OpportunityFeedbackForm';
	import MemberCard from '../Member/MemberCard';
    import AttachmentDownload from '../AttachmentDownload';

	export default {
		name: 'OpportunityView',
		mixins: [ResourceView],
		components: {
            AttachmentDownload,
			MemberCard,
			OpportunityFeedbackForm,
			OpportunityResponseForm,
			UserCard,
			Accordion,
			SvgIcon,
			LayoutBase,
			Response,
			FileUpload
		},
		data() {
			return {
				resource: {
					member: {
						country: null,
						logo: null
					},
					sectors: [],
					team: []
				},
			}
		},
		computed: {
			respondBy() {
				return this.resource.respondBy ? format(this.resource.respondBy, 'dd/MM/yy') : null;
			},
			ableToHelp() {
				return this.resource && this.resource.responses ?
					this.resource.responses.filter(response => response.canHelp) : [];
			},
			unableToHelp() {
				return this.resource && this.resource.responses ?
					this.resource.responses.filter(response => !response.canHelp) : [];
			},
			responseRate() {
				if(this.resource && this.resource.responses) {
					let responseCount = this.resource.responses.length;
					let positiveResponseCount = this.resource.responses.filter(response => response.canHelp).length;
					// let negativeResponseCount = this.resource.responses.filter(response => !response.canHelp).length;
					return ((positiveResponseCount / responseCount) * 100).toFixed(2) + '%';
				} else {
					return null;
				}
			}
		},
        watch: {
            id: function (newVal, oldVal) {
                return API.getOpportunity(newVal).then(response => {
                    console.log("[OpportunityView.vue] - ", response);
                    this.resource = response;
                });
            }
        },
		methods: {
			fetchResource() {
                return API.getOpportunity(this.id);
            },
			formatAccordionData() {
				let items = [];

				if(!this.resource) return items;

				if(this.resource.description){
					items.push({ title : 'Company bio', content : this.resource.description });
				}

				if(this.resource.context){
					items.push({ title : 'Context', content : this.resource.context });
				}

				if(this.resource.buyerExamples){
					items.push({ title : 'Buyer examples', content : this.resource.buyerExamples });
				}

				return items;
			},
			filterLead(users){
				return users.filter(user => {
					return user.id !== this.resource.lead.id;
				});
			},
            addResponse(response) {
	            this.resource.responses.push(response);
	            let target;

	            if(response.type === 1) {
                    if(typeof this.$refs.ableToHelp !== 'undefined') {
                        target = this.$refs.ableToHelp[this.$refs.ableToHelp.length - 1].$el;
                    }
                } else {
	                if(typeof this.$refs.unableToHelp !== 'undefined'){
	            	    target = this.$refs.unableToHelp[this.$refs.unableToHelp.length - 1].$el;
                    }
                }

	            target.scrollIntoView({behavior: 'smooth', block: 'start'});
            },
            updateResponse(updatedResponse){
			    let target;

                this.resource.responses = this.resource.responses.map(response => {
                    if(response.id === updatedResponse.id){
                        console.log('updatedresponse', updatedResponse);
                        response = Object.assign(response, updatedResponse);
                        console.log('response', response);
                    }
                    return response;
                });

	            if(updatedResponse.canHelp) {
	                target = this.$refs.ableToHelp.filter(responseComp => {
	                    return responseComp.response.id === updatedResponse.id;
                    });
                } else {
	                target = this.$refs.unableToHelp.filter(responseComp => {
	                    return responseComp.response.id === updatedResponse.id;
                    });
                }

	            if(target.length){
                    target = target[0].$el;
	                target.scrollIntoView({behavior: 'smooth', block: 'start'});
                }

            },
            addFeedback(feedback) {
				this.resource.feedback = feedback;
				this.resource.can.feedback = false;
            },
            responseDeleted(responseDeleted){
                this.resource.responses = this.resource.responses.filter(response => response.id !== responseDeleted.id);
            },
            populateForm(response){
                console.log(response);
                this.$refs.responseForm.formData = {
                    id              : response.id,
                    opportunity_id  : response.opportunity_id,
                    ableToHelp      : response.canHelp,
                    content         : response.raw_content,
                    hasAttachments  : response.attachments.length || false,
                    documents       : response.attachments.map(file => file.uuid)
                }
                console.log(this.$refs.responseForm.formData);
            }
		}
	}
</script>

<style lang="scss" scoped>
	@import '~reflex-grid/scss/includes/variables';
	@import '~reflex-grid/scss/includes/mixins';
	@import '~@/theme/_variables.scss';
	@import '~@/mixins/_class.scss';
	#content{
		#content__header{
			> h1{
                margin-bottom: 0;
                margin-right: 0 !important;

                + .pill,
                + .respond-by{
                    margin-left: 2rem;
                }
			}

            .actions{
                margin-left: auto;
                margin-right: 0;
            }

			.pill + span{
				color: $accent-error;
				margin-left: 1rem;
				margin-top: 0.2rem;
			}

			.respond-by{
				color: #E42034;
			}
		}
	}
	.deal-info{
		margin-bottom: 5rem;

		@include respond-to(medium){
    		margin-bottom: 1rem;
		}

		.sector + .sector {
			margin-left: 0.5rem;
		}
	}
</style>
